import { Course, UserProfile } from '../types';

/**
 * Gets the study IDs from a course
 * @param course - The course
 * @returns Array of study IDs
 */
function getCourseStudyIds(course: Course): string[] {
  return course.studyIds || [];
}

/**
 * Checks if a study is unlocked for a user based on course requirements
 * @param studyId - The ID of the study to check
 * @param course - The course that contains the study (or null if study is not part of a course)
 * @param user - The user profile
 * @returns true if the study is unlocked, false otherwise
 */
export function isStudyUnlockedInCourse(
  studyId: string,
  course: Course | null,
  user: UserProfile
): boolean {
  // If study is not part of a course, it's always unlocked
  if (!course) {
    return true;
  }

  const studyIds = getCourseStudyIds(course);

  // Find the index of this study in the course
  const studyIndex = studyIds.indexOf(studyId);

  // If study is not in the course, treat as unlocked
  if (studyIndex === -1) {
    return true;
  }

  // First study is always unlocked
  if (studyIndex === 0) {
    return true;
  }

  // If sequential viewing is not required, all studies are unlocked
  if (!course.requireSequentialViewing) {
    return true;
  }

  // Check if all previous studies are completed
  for (let i = 0; i < studyIndex; i++) {
    const previousStudyId = studyIds[i];
    if (!user.completedStudyIds?.includes(previousStudyId)) {
      return false;
    }
  }

  return true;
}

/**
 * Finds which course a study belongs to (if any)
 * @param studyId - The ID of the study
 * @param courses - Array of courses to search through
 * @returns The course containing the study, or null if not found
 */
export function findCourseForStudy(
  studyId: string,
  courses: Course[]
): Course | null {
  return courses.find(course => getCourseStudyIds(course).includes(studyId)) || null;
}
